/**
 * AllBaise Bottom Navigation — fixed bar with 9 hub links.
 * Visible on all screen sizes. Active state highlights current route.
 */
import { Link, useLocation } from "react-router-dom";
import {
  Home, Briefcase, MessageSquare, LayoutDashboard, Users,
  Building2, Gift, GraduationCap, Star, Brain,
} from "lucide-react";
import { useHubAccess } from "@/services/hubAccess";

const NAV_ITEMS = [
  { to: "/", label: "Home", icon: Home },
  { to: "/smart-office", label: "Office Tools", icon: Briefcase, requiredHub: "smart_office" },
  { to: "/comms-centre", label: "Comms Centre", icon: MessageSquare, requiredHub: "comms" },
  { to: "/dashboard", label: "Corporate Tools", icon: LayoutDashboard, requiredHub: "dashboard" },
  { to: "/people-directory", label: "My People", icon: Users },
  { to: "/corporate-hub", label: "My Orgs", icon: Building2 },
  { to: "/deals", label: "Deals", icon: Gift },
  { to: "/career", label: "Career Hub", icon: GraduationCap },
  { to: "/wish-centre", label: "Wish Centre", icon: Star },
  { to: "/agents-centre", label: "Agents", icon: Brain },
];

export default function BottomNav() {
  const location = useLocation();
  const hubAccess = useHubAccess();
  const filteredItems = NAV_ITEMS.filter(item => !item.requiredHub || hubAccess.hasAccess(item.requiredHub));

  const isActive = (itemTo) => {
    if (itemTo === "/") return location.pathname === "/";
    return location.pathname.startsWith(itemTo);
  };

  return (
    <>
      {/* Spacer to push content above the fixed bar */}
      <div className="h-16 md:h-14" />

      {/* Fixed Bottom Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-app border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          {/* Desktop: full bar, all items visible */}
          <div className="hidden md:flex items-center justify-around h-14 px-4">
            {filteredItems.map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    active
                      ? "text-violet-400 bg-violet-600/10"
                      : "text-zinc-500 hover:text-zinc-300 hover:bg-white/5"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Mobile: 3×3 grid */}
          <div className="grid grid-cols-5 gap-0 p-1 md:hidden">
            {filteredItems.slice(0, 5).map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex flex-col items-center gap-0.5 py-2 rounded-lg text-[10px] font-medium transition-colors ${
                    active
                      ? "text-violet-400"
                      : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${active ? "text-violet-400" : ""}`} />
                  <span className="truncate max-w-full">{item.label}</span>
                </Link>
              );
            })}
            {/* Second row: items 5-8 plus empty cell for centering */}
            {filteredItems.slice(5).map((item) => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex flex-col items-center gap-0.5 py-2 rounded-lg text-[10px] font-medium transition-colors ${
                    active
                      ? "text-violet-400"
                      : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${active ? "text-violet-400" : ""}`} />
                  <span className="truncate max-w-full">{item.label}</span>
                </Link>
              );
            })}
            {/* Empty cells to fill 5-column grid for row 2 */}
            {Array.from({ length: 5 - (filteredItems.length - 5) }).map((_, i) => (
              <div key={`spacer-${i}`} />
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}
